import { useEffect } from "react";
import { useTasks, useUI } from "../hooks/index.js";

const BubbleVisualizer = () => {
  const { tasks, setTasks, hasRun, setHasRun } = useTasks();
  const { isMobile } = useUI();
  useEffect(() => {
    if (hasRun || tasks.length === 0) return; // Prevent rerunning if already done or tasks are empty

    const today = new Date().setHours(0, 0, 0, 0);

    // Sort task1 (impact desc, effort desc, due date asc while ignoring null), then shuffle the remainder
    const sortedTasks = [...tasks].sort((a, b) => {
      if (b.impact !== a.impact) {
        return b.impact - a.impact;
      } else if (b.effort !== a.effort) {
        return b.effort - a.effort;
      } else if (a.due_date === null && b.due_date === null) {
        return 0;
      } else if (a.due_date === null) {
        return 1;
      } else if (b.due_date === null) {
        return -1;
      } else {
        return new Date(a.due_date).getTime() - new Date(b.due_date).getTime();
      }
    });
    const task1 = sortedTasks.shift(); // Extract the first task (task1)
    for (let i = sortedTasks.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [sortedTasks[i], sortedTasks[j]] = [sortedTasks[j], sortedTasks[i]];
    }
    const shuffledTasks = [task1, ...sortedTasks];

    // Determine urgency and mark all overdue tasks as urgent
    shuffledTasks.forEach((task) => {
      if (task.due_date && new Date(task.due_date) < today) {
        task.urgent = true;
      } else {
        task.urgent = false;
      }
    });

    // Find the next large task due (not overdue) and mark it as urgent
    const nonOverdueTasks = shuffledTasks.filter(
      (task) => task.due_date && new Date(task.due_date) >= today
    );

    if (nonOverdueTasks.length > 0) {
      const nextTask = nonOverdueTasks.sort((a, b) => {
        const dateDiff =
          new Date(a.due_date).getTime() - new Date(b.due_date).getTime();
        if (dateDiff !== 0) return dateDiff;
        return b.effort - a.effort; // Larger effort first
      })[0];
      nextTask.urgent = true; // Mark as urgent
    }

    const center = { x: window.innerWidth / 2, y: 0 };
    let bubbles = [];
    const angleIncrement = 15; // Angle increment in degrees
    let ringRadius = [0]; // Array to track max radius at each ring index

    // Function to determine if a bubble overlaps with existing bubbles
    const bubblesOverlap = (x, y, radius) => {
      for (const bubble of bubbles) {
        const distance = Math.sqrt(
          Math.pow(bubble.x - x, 2) + Math.pow(bubble.y - y, 2)
        );
        if (distance < bubble.radius + radius) {
          return true;
        }
      }
      return false;
    };

    // Step 2: Place task1 at x, y (the centerpoint)
    task1.x = center.x;
    task1.y = center.y;
    task1.radius = 30 + (task1.effort / 100) * 40;
    bubbles.push({ x: center.x, y: center.y, radius: task1.radius });

    // Step 3: Place task 2 at random initialAngle, at distance from centerpoint = task1.radius + task2.radius
    const initialAngle = Math.floor(Math.random() * 360);
    let currentRingIndex = 1;
    ringRadius[currentRingIndex] = task1.radius;

    // Iterate through the remaining tasks to place them
    for (let i = 1; i < shuffledTasks.length; i++) {
      const currentTask = shuffledTasks[i];
      const radius = 30 + (currentTask.effort / 100) * 40;
      let placed = false;
      let angle = initialAngle;
      let maxLoops = Math.floor(360 / angleIncrement) - 1;
      let attempts = 0;

      while (!placed) {
        // Calculate the position based on current max radius at this angle
        const radians = (angle * Math.PI) / 180;
        let distanceFromCenter = ringRadius[currentRingIndex] + radius;
        let x = center.x + distanceFromCenter * Math.cos(radians);
        let y = center.y + distanceFromCenter * Math.sin(radians);

        // Force bubbles overlapping left or right edge to try a shift to stay on screen
        const minX = radius; // Leftmost allowance
        const maxX = window.innerWidth - radius; // Rightmost allowance
        const minY = isMobile ? -120 + radius : -200 + radius; // TODO: can this be done more dynamically?

        if (x < minX) x = minX; // If too close shift right
        if (x > maxX) x = maxX; // If too close shift left
        if (y < minY) y = minY; // If too high shift down

        // Check for overlap with other bubbles
        if (!bubblesOverlap(x, y, radius)) {
          // Place the bubble
          currentTask.x = x;
          currentTask.y = y;
          currentTask.radius = radius;
          bubbles.push({ x, y, radius });

          placed = true;
        } else {
          // Increment angle to try again
          angle += angleIncrement;
          attempts++;

          // If reached max loops, move to the next ring
          if (attempts >= maxLoops) {
            currentRingIndex++;
            if (!ringRadius[currentRingIndex]) {
              ringRadius[currentRingIndex] =
                ringRadius[currentRingIndex - 1] + radius;
            }
            attempts = 0; // Reset attempts for the new ring
          }
        }
      }
    }

    setTasks([...shuffledTasks]);
    setHasRun(true); // Mark as run to avoid re-running - important or this creates infinite processing loop
  }, [tasks, setTasks, hasRun, setHasRun]);

  return null;
};

export default BubbleVisualizer;
